import {  ILoginResponse, IUser, IUserInfo, IUserLoginPayload  } from "../../../types/api/user";
import { TResponse, TResponseWithQData } from "../../../types/common";
import { useMutation, UseMutationOptions, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { createUser, deleteUsers, getUserInfo, RegisteredUserList, userDetails, userLogin, userLogoutApi, userUpdatedProfile, userUpdatePassword, validateToken } from "./callbacks";
import { message } from "antd";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { QData } from "../../../types/basic-type-list";
import { useState } from "react";
import { IRegisteredUserListResponse } from "../../../types/api/users-manage";
import { IDeleteDataResponseAndPayload } from "../../../types/api/contact";

export const useUserLogin = (
  options: UseMutationOptions<
    TResponse<ILoginResponse>,
    Error,
    IUserLoginPayload
  > = {}
): UseMutationResult<
  TResponse<ILoginResponse>,
  Error,
  IUserLoginPayload
> => {
  return useMutation({
    mutationFn: async (payload: IUserLoginPayload) => {
      const response = await userLogin(payload);
      return response.data;
    },
    onSuccess: (res, ...rest) => {
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error: Error, ...rest) => {
      options?.onError?.(error, ...rest);
    },
    ...options,
  });
};

export const useUserLogout = (
  options: UseMutationOptions<TResponse<null>, Error, void> = {}
): UseMutationResult<TResponse<null>, Error, void> => {
  return useMutation({
    mutationFn: async () => {
      const response = await userLogoutApi();
      return response.data;
    },
    ...options,
  });
};
export const useGetUserInfo = (
  options: Omit<
    UseQueryOptions<TResponse<IUserInfo | null>, Error>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TResponse<IUserInfo | null>, Error> => {
  const queryResult = useQuery({
    queryKey: [API_ENDPOINTS.ADMIN_DETAIL],
    queryFn: async () => {
      const res = await getUserInfo();
      return res.data;
    },
    initialData: { data: null, success: false, message: "", code: 500 },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    retry: false,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
  };
};

export const useValidateToken = (
  options: Omit<
    UseQueryOptions<TResponse<IUserInfo | null>, Error>,
    "queryKey" | "queryFn"
  > = {}
): UseQueryResult<TResponse<IUserInfo | null>, Error> => {
  const queryResult = useQuery({
    queryKey: [API_ENDPOINTS.VALIDATE_TOKEN],
    queryFn: async () => {
      const res = await validateToken();
      return res.data;
    },
    initialData: { data: null, success: false, message: "", code: 500 },
    staleTime: 300000,
    initialDataUpdatedAt: 0,
    // retry : false,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
  };
};

export const useUserProfileUpdate = (
  { onSuccess, onError }: {
    onSuccess?: (res: TResponse<IUser>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IUser>, unknown, IUser> = {}
): UseMutationResult<TResponse<IUser>, unknown, IUser> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IUser>, unknown, IUser>({
    mutationFn: async (data: any) => {
      const response = await userUpdatedProfile(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
     
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.ADMIN_DETAIL],
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};

export const useUserPasswordUpdate = (
  { onSuccess, onError }: {
    onSuccess?: (res: TResponse<IUser>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IUser>, unknown, IUser> = {}
): UseMutationResult<TResponse<IUser>, unknown, IUser> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IUser>, unknown, IUser>({
    mutationFn: async (data: any) => {
      const response = await userUpdatePassword(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.invalidateQueries({
        queryKey: [API_ENDPOINTS.ADMIN_DETAIL],
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};




// RegisteredUserList

export const useRegisteredUserList = (
  qData: QData,
  options?: Omit<
    UseQueryOptions<TResponseWithQData<IRegisteredUserListResponse[]>, Error>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<TResponseWithQData<IRegisteredUserListResponse[]>, Error> & {
  refetchWithQuery: (params: Partial<QData>) => void;
} => {
  const [queryParams, setQueryParams] = useState<QData>({ ...qData });

  const queryResult = useQuery<TResponseWithQData<IRegisteredUserListResponse[]>, Error>({
    queryKey: [API_ENDPOINTS.REGISTERED_USER_LIST, { ...queryParams, total: null }],
    queryFn: async () => {
      const res = await RegisteredUserList(queryParams);
      return res.data;
    },
    select: (res) => ({
      ...res,
      data: res.data.map(x => ({ key: x._id, ...x })),
      qData: { ...queryParams, total: res.extra?.total }
    }),
    initialData: {
      data: [],
      success: true,
      message: '',
      code: 200,
      qData: { page: 1, limit: 20 },
      extra: { page: 1, limit: 20, total: 0 },
    },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
    ...options,
  });

  return {
    ...queryResult,
    refetchWithQuery: (params: Partial<QData>) => {
      setQueryParams((prev: any) => ({ ...prev, ...params }));
    },
  };
};


// create user
export const useCreateUser = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IRegisteredUserListResponse>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IRegisteredUserListResponse>, unknown, IRegisteredUserListResponse> = {}
): UseMutationResult<TResponse<IRegisteredUserListResponse>, unknown, IRegisteredUserListResponse> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IRegisteredUserListResponse>, unknown, IRegisteredUserListResponse>({
    mutationFn: async (data: IRegisteredUserListResponse) => {
      const response = await createUser(data);
      return response.data;
    },
    onSuccess: (res: any, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.REGISTERED_USER_LIST, { ...qData, total: null }], (old: any) => {
        if (!old) return;
        const exists = old.data.find((v: any) => v._id === res.data._id);
        return {
          ...old,
          data: exists  
            ? old.data.map((v: any) => (v._id === res.data._id ? res.data : v))
            : [res.data, ...old.data]
        };
      });

       queryClient.setQueryData(
        [API_ENDPOINTS.REGISTERED_USER_DETAILS, res.data._id],
        {
          success: true,
          message: "",
          code: 200,
          data: res.data,
        }
      );

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
      },
      onError: (error, ...rest) => {
        onError?.(error);
        options?.onError?.(error, ...rest);
      },
      ...options
    });
};


export const useGetUserDetails = (
  id: string,
  options?: Omit<
    UseQueryOptions<TResponse<IRegisteredUserListResponse>, Error>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<TResponse<IRegisteredUserListResponse>, Error> => {
  return useQuery<TResponse<IRegisteredUserListResponse>, Error>({
    queryKey: [API_ENDPOINTS.REGISTERED_USER_DETAILS, id],
    queryFn: async () => {
      const res = await userDetails(id);
      return res.data;
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 300000,
    ...options,
  });
};


export const useRegisteredUserDelete = (
  { qData = { page: 1, limit: 20 }, onSuccess, onError }: {
    qData?: QData;
    onSuccess?: (res: TResponse<IDeleteDataResponseAndPayload>) => void;
    onError?: (error: unknown) => void;
  }
  ,
  options: UseMutationOptions<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> = {}
): UseMutationResult<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload> => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IDeleteDataResponseAndPayload>, unknown, IDeleteDataResponseAndPayload>({
    mutationFn: async (data: IDeleteDataResponseAndPayload) => {
      const response = await deleteUsers(data);
      return response.data;
    },
    onSuccess: (res: TResponse<IDeleteDataResponseAndPayload>, ...rest) => {
      queryClient.setQueryData([API_ENDPOINTS.REGISTERED_USER_LIST, { ...qData, total: null }], (old: TResponse<IDeleteDataResponseAndPayload[]>) => {
        if (!old) return;

        const newTotal = old.extra?.total ? old.extra.total - 1 : 1;

        return {
          ...old,
          data: Array.isArray(res.data.ids) ? old.data.filter((v: any) => ~res.data.ids.includes(v._id)) : old.data.filter((v: any) => res.data.ids !== v._id),
          extra: {
            ...old.extra,
            total: newTotal
          }
        };
      });

      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },
    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },
    ...options
  });
};

export const useUpdateRegisteredUser = (
  options?: UseMutationOptions<
    TResponse<IRegisteredUserListResponse>,
    Error,
    { id: string; data: Partial<IRegisteredUserListResponse> }
  >,
): UseMutationResult<
  TResponse<IRegisteredUserListResponse>,
  Error,
  { id: string; data: Partial<IRegisteredUserListResponse> }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const { updateRegisteredUser } = await import("./callbacks");
      const res = await updateRegisteredUser(id, data);
      return res.data;
    },
    onSuccess: (res: any, ...rest: any[]) => {
      const updatedUser = res?.data;
      if (updatedUser && updatedUser._id) {
        queryClient.setQueriesData(
          { queryKey: [API_ENDPOINTS.REGISTERED_USER_LIST] },
          (old: any) => {
            if (!old || !old.data) return old;
            return {
              ...old,
              data: old.data.map((item: any) =>
                item._id === updatedUser._id ? { ...item, ...updatedUser } : item,
              ),
            };
          },
        );
      } else {
        queryClient.invalidateQueries({ queryKey: [API_ENDPOINTS.REGISTERED_USER_LIST] });
      }
      (options?.onSuccess as any)?.(res, ...rest);
    },
    ...options,
  });
};

export const useUpdateRegisteredUserPassword = (
  options?: UseMutationOptions<
    TResponse<any>,
    Error,
    { id: string; password: string }
  >,
): UseMutationResult<
  TResponse<any>,
  Error,
  { id: string; password: string }
> => {
  return useMutation({
    mutationFn: async ({ id, password }) => {
      const { updateRegisteredUserPassword } = await import("./callbacks");
      const res = await updateRegisteredUserPassword(id, password);
      return res.data;
    },
    ...options,
  });
};
